/**
 * How a field error binds to an input — one rule for `fieldErrorFor`, the
 * field errors of a dialog (`useFieldErrors`) and the decider
 * (`reportWriteFailure`), which must agree on which field errors an input
 * shows and which are left over as error summary entries (spec I-2813 K16).
 * Internal to the package: the HTTP index does not export this file.
 */
import type { ProblemFieldError, ProblemFieldTarget } from './problem';
import type { FieldErrorRecord } from './problemHelpers';

/** Whether target names the field key of exactly this record (id 0 names none). */
export function targetNames(
  target: ProblemFieldTarget,
  key: string,
  record: FieldErrorRecord
): boolean {
  return (
    target.field === key &&
    target.resource === record.resource &&
    target.id !== 0 &&
    String(target.id) === String(record.id)
  );
}

/**
 * Whether the field error binds to one of the keys: by its dotted key
 * (pointer), or by its target when the inputs' record is given.
 */
export function bindsToAny(
  fieldError: ProblemFieldError,
  keys: readonly string[],
  record?: FieldErrorRecord
): boolean {
  if (fieldError.key !== undefined && keys.includes(fieldError.key)) return true;
  const target = fieldError.target;
  return !!record && !!target && keys.some((key) => targetNames(target, key, record));
}
